import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Dashboard.css";
import Navbar from "../Component/Common/Navbar";
import { connectRealtimeSocket } from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   DEFAULT DATA
============================== */
const DEFAULT_USER = {
  fullName: "User",
  email: "",
  status: "Active User",
  plan: "Starter Plan",
  joined: "Member since 2026",
};

const DEFAULT_STATS = [
  {
    title: "My Domains",
    value: "00",
    desc: "Active domains under your account",
    icon: "🌐",
    trend: "No recent updates",
    link: "/domains",
  },
  {
    title: "Hosting Services",
    value: "00",
    desc: "No active hosting service",
    icon: "🚀",
    trend: "Choose a hosting plan",
    link: "/hosting",
  },
  {
    title: "Active Orders",
    value: "00",
    desc: "Orders currently in progress",
    icon: "📦",
    trend: "No recent updates",
    link: "/orders",
  },
  {
    title: "Wallet / Credits",
    value: "₹0",
    desc: "Available balance for purchases",
    icon: "💳",
    trend: "No recent updates",
    link: "/wallet",
  },
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(
    new CustomEvent("auth-state-changed", {
      detail: {
        source: "UserDashboard",
        action: "clear-auth",
      },
    })
  );
};

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

const getStoredUser = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return {};

  try {
    const parsed = JSON.parse(rawUser);

    if (!parsed || typeof parsed !== "object") return {};

    return parsed;
  } catch {
    return {};
  }
};

/* ==============================
   FORMATTERS
============================== */
const formatMembership = (createdAt) => {
  if (!createdAt) return "Member since 2026";

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) return "Member since 2026";

  return `Member since ${date.getFullYear()}`;
};

const normalizeUser = (profile = {}) => {
  const storedUser = getStoredUser();

  const fullName =
    profile.fullName ||
    profile.full_name ||
    profile.name ||
    profile.username ||
    storedUser.fullName ||
    storedUser.full_name ||
    storedUser.name ||
    storedUser.username ||
    localStorage.getItem("fullName") ||
    localStorage.getItem("name") ||
    localStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    "User";

  const email =
    profile.email ||
    storedUser.email ||
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    localStorage.getItem("user_email") ||
    "";

  const status = profile.status || storedUser.status || "Active User";

  const plan =
    profile.plan ||
    profile.planName ||
    profile.plan_name ||
    storedUser.plan ||
    storedUser.planName ||
    "Starter Plan";

  const createdAt =
    profile.createdAt ||
    profile.created_at ||
    storedUser.createdAt ||
    storedUser.created_at ||
    "";

  return {
    fullName: String(fullName || "User").trim(),
    email: String(email || "").trim().toLowerCase(),
    status: String(status || "Active User").trim(),
    plan: String(plan || "Starter Plan").trim(),
    joined: formatMembership(createdAt),
  };
};

const normalizeStats = (stats = []) => {
  if (!Array.isArray(stats) || stats.length === 0) return DEFAULT_STATS;

  return stats.map((item, index) => ({
    id: item?.id || `stat-${index}`,
    title: item?.title || DEFAULT_STATS[index]?.title || "Dashboard Item",
    value: item?.value ?? DEFAULT_STATS[index]?.value ?? "00",
    desc: item?.desc || item?.description || DEFAULT_STATS[index]?.desc || "",
    icon: item?.icon || DEFAULT_STATS[index]?.icon || "•",
    trend: item?.trend || DEFAULT_STATS[index]?.trend || "No recent updates",
    link: item?.link || DEFAULT_STATS[index]?.link || "/dashboard",
    color: item?.color || "blue",
  }));
};

const normalizeActivities = (activities = []) => {
  if (!Array.isArray(activities)) return [];

  return activities.map((activity, index) => ({
    id: activity?.id || `${activity?.title || "activity"}-${index}`,
    title: activity?.title || "Activity",
    desc: activity?.desc || activity?.description || "No details available.",
    type: activity?.type || activity?.activityType || "default",
    time:
      activity?.time ||
      activity?.createdAtFormatted ||
      activity?.createdAt ||
      activity?.created_at ||
      "Recently",
  }));
};

const normalizeHostingService = (service = null) => {
  if (!service || typeof service !== "object") return null;

  return {
    id: service.id || null,
    planKey: service.planKey || service.plan_key || "",
    planName: service.planName || service.plan_name || "Hosting Plan",
    price: service.price || "₹0",
    rawPrice: Number(service.rawPrice || service.price || 0),
    billingCycle: service.billingCycle || service.billing_cycle || "monthly",
    status: service.status || "active",
    isActive:
      service.isActive !== undefined
        ? Boolean(service.isActive)
        : String(service.status || "active").toLowerCase() === "active",
    setupStatus: service.setupStatus || service.setup_status || "pending_setup",
    setupStatusLabel:
      service.setupStatusLabel ||
      (service.setupStatus === "completed" || service.setup_status === "completed"
        ? "Completed"
        : service.setupStatus === "in_progress" ||
          service.setup_status === "in_progress"
        ? "In Progress"
        : "Pending Setup"),
    websiteLimit: Number(service.websiteLimit || service.website_limit || 0),
    storageLimit: service.storageLimit || service.storage_limit || "",
    mailboxLimit: Number(service.mailboxLimit || service.mailbox_limit || 0),
    sslEnabled: Boolean(
      service.sslEnabled !== undefined ? service.sslEnabled : service.ssl_enabled
    ),
    cpanelUrl: service.cpanelUrl || service.cpanel_url || "",
    cpanelUsername: service.cpanelUsername || service.cpanel_username || "",
    serverIp: service.serverIp || service.server_ip || "",
    nameserver1: service.nameserver1 || service.nameserver_1 || "",
    nameserver2: service.nameserver2 || service.nameserver_2 || "",
    startedAt: service.startedAt || service.started_at || "",
    expiresAt: service.expiresAt || service.expires_at || "",
    message:
      service.message ||
      "Your hosting plan is active. Complete setup to connect your domain and website details.",
  };
};

const getActivityIcon = (type) => {
  const clean = String(type || "").toLowerCase();

  if (clean.includes("search")) return "🔎";
  if (clean.includes("order")) return "📦";
  if (clean.includes("security")) return "🔐";
  if (clean.includes("favorite")) return "⭐";
  if (clean.includes("wallet") || clean.includes("payment")) return "💳";
  if (clean.includes("domain")) return "🌐";
  if (clean.includes("hosting")) return "🚀";
  if (clean.includes("dns")) return "🧭";

  return "•";
};

const getRealtimeLabel = (status) => {
  if (status === "connected") return "Realtime connected";
  if (status === "connecting") return "Connecting realtime...";
  return "Realtime offline";
};

const getSetupClass = (status = "") => {
  const clean = String(status || "").toLowerCase();

  if (clean === "completed") return "completed";
  if (clean === "in_progress") return "progress";
  if (clean === "failed") return "failed";

  return "pending";
};

function UserDashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(DEFAULT_USER);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [recentActivities, setRecentActivities] = useState([]);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");

  const [hostingService, setHostingService] = useState(null);
  const [summary, setSummary] = useState({});
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentDomains, setRecentDomains] = useState([]);

  const quickActions = useMemo(
    () => [
      {
        title: "Search Domains",
        desc: "Find .com, .in, .org and more",
        link: "/domains",
        badge: "Popular",
      },
      {
        title: "Hosting Plans",
        desc: "Buy and activate secure hosting",
        link: "/hosting",
        badge: "Live",
      },
      {
        title: "Complete Hosting Setup",
        desc: "Submit domain and website setup details",
        link: "/hosting/setup",
        badge: "Setup",
      },
      {
        title: "Payment History",
        desc: "Check your Razorpay transactions",
        link: "/payment-history",
        badge: "Secure",
      },
      {
        title: "DNS Manage",
        desc: "Manage domain DNS records",
        link: "/dns-manage",
        badge: "Tools",
      },
      {
        title: "Profile Settings",
        desc: "Manage your personal details",
        link: "/profile",
        badge: "Update",
      },
    ],
    []
  );

  /* ==============================
     PROTECTED DASHBOARD FETCH
  ============================== */
  const fetchDashboard = useCallback(
    async (signal, silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setError("");

        const token = getToken();

        if (!token) {
          clearAuth();
          navigate("/signin", { replace: true });
          return;
        }

        const authHeaders = {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        };

        const [profileResponse, dashboardResponse] = await Promise.all([
          fetch(`${API_BASE}/auth/me`, {
            method: "GET",
            headers: authHeaders,
            signal,
            cache: "no-store",
          }),
          fetch(`${API_BASE}/dashboard`, {
            method: "GET",
            headers: authHeaders,
            signal,
            cache: "no-store",
          }),
        ]);

        if (
          profileResponse.status === 401 ||
          profileResponse.status === 403 ||
          dashboardResponse.status === 401 ||
          dashboardResponse.status === 403
        ) {
          clearAuth();
          navigate("/signin", { replace: true });
          return;
        }

        const profileResult = await safeJson(profileResponse);
        const dashboardResult = await safeJson(dashboardResponse);

        if (!profileResponse.ok || profileResult?.success === false) {
          throw new Error(profileResult?.message || "Failed to load user profile.");
        }

        if (!dashboardResponse.ok || dashboardResult?.success === false) {
          throw new Error(dashboardResult?.message || "Failed to load dashboard.");
        }

        const profile = profileResult?.data || profileResult?.user || {};
        const dashboardData = dashboardResult?.data || {};

        const nextUser = normalizeUser(profile);
        const nextStats = normalizeStats(dashboardData?.stats);
        const nextActivities = normalizeActivities(dashboardData?.activities);
        const nextHostingService = normalizeHostingService(
          dashboardData?.hostingService ||
            dashboardData?.serviceAccess?.hosting?.service ||
            null
        );

        setUser(nextUser);
        setStats(nextStats);
        setRecentActivities(nextActivities);
        setHostingService(nextHostingService);
        setSummary(dashboardData?.summary || {});
        setRecentPayments(
          Array.isArray(dashboardData?.recentPayments)
            ? dashboardData.recentPayments
            : []
        );
        setRecentDomains(
          Array.isArray(dashboardData?.recentDomains)
            ? dashboardData.recentDomains
            : []
        );

        localStorage.setItem(
          "user",
          JSON.stringify({
            fullName: nextUser.fullName,
            email: nextUser.email,
            status: nextUser.status,
            plan: nextUser.plan,
            createdAt: profile.createdAt || profile.created_at || "",
          })
        );

        localStorage.setItem("fullName", nextUser.fullName);
        localStorage.setItem("email", nextUser.email);

        window.dispatchEvent(
          new CustomEvent("auth-state-changed", {
            detail: {
              source: "UserDashboard",
              action: "dashboard-sync",
            },
          })
        );
      } catch (err) {
        if (err?.name === "AbortError") return;

        setError(err?.message || "Unable to load dashboard.");
        setUser(normalizeUser());
        setStats(DEFAULT_STATS);
        setRecentActivities([]);
        setHostingService(null);
        setSummary({});
        setRecentPayments([]);
        setRecentDomains([]);
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchDashboard(controller.signal);

    return () => controller.abort();
  }, [fetchDashboard]);

  /* ==============================
     REALTIME SOCKET
     Do not disconnect shared socket on unmount.
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;
    let controller = null;

    const applyDashboardPayload = (dashboardData = {}) => {
      if (Array.isArray(dashboardData?.stats)) {
        setStats(normalizeStats(dashboardData.stats));
      }

      if (Array.isArray(dashboardData?.activities)) {
        setRecentActivities(normalizeActivities(dashboardData.activities));
      }

      if (dashboardData?.hostingService || dashboardData?.serviceAccess?.hosting?.service) {
        setHostingService(
          normalizeHostingService(
            dashboardData?.hostingService ||
              dashboardData?.serviceAccess?.hosting?.service
          )
        );
      }

      if (dashboardData?.summary) {
        setSummary(dashboardData.summary);
      }

      if (Array.isArray(dashboardData?.recentPayments)) {
        setRecentPayments(dashboardData.recentPayments);
      }

      if (Array.isArray(dashboardData?.recentDomains)) {
        setRecentDomains(dashboardData.recentDomains);
      }
    };

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        if (controller) controller.abort();

        controller = new AbortController();
        fetchDashboard(controller.signal, true);
      }, 500);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleDashboardUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      applyDashboardPayload(payload?.data || {});
      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleHostingStatus = (payload = {}) => {
      setRealtimeStatus("connected");

      if (payload?.planName) {
        setHostingService((prev) =>
          normalizeHostingService({
            ...(prev || {}),
            planKey: payload.planKey,
            planName: payload.planName,
            price: payload.price,
            billingCycle: payload.billingCycle,
            status: payload.status || "active",
            setupStatus: payload.setupStatus || "pending_setup",
            setupStatusLabel:
              payload.setupStatus === "completed"
                ? "Completed"
                : payload.setupStatus === "in_progress"
                ? "In Progress"
                : "Pending Setup",
            websiteLimit: payload.limits?.websites || prev?.websiteLimit || 0,
            storageLimit: payload.limits?.storage || prev?.storageLimit || "",
            mailboxLimit: payload.limits?.mailboxes || prev?.mailboxLimit || 0,
            sslEnabled:
              payload.limits?.ssl !== undefined
                ? payload.limits.ssl
                : prev?.sslEnabled,
            message: payload.message,
          })
        );
      }

      softRefresh();
    };

    const handleSocketAuthError = () => {
      clearAuth();
      navigate("/signin", { replace: true });
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("dashboard:update", handleDashboardUpdate);
    socket.on("notification:new", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);

    socket.on("payment:status", handleRealtimeRefresh);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("hosting:status", handleHostingStatus);
    socket.on("domain:availability:update", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      if (controller) controller.abort();

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("dashboard:update", handleDashboardUpdate);
      socket.off("notification:new", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);

      socket.off("payment:status", handleRealtimeRefresh);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("hosting:status", handleHostingStatus);
      socket.off("domain:availability:update", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);
    };
  }, [fetchDashboard, navigate]);

  /* ==============================
     UI MEMO DATA
  ============================== */
  const greeting = useMemo(() => {
    const currentHour = new Date().getHours();

    if (currentHour < 12) return "Good morning";
    if (currentHour < 17) return "Good afternoon";

    return "Good evening";
  }, []);

  const userInitials = useMemo(() => {
    const name = user.fullName || "User";
    const words = name.trim().split(" ").filter(Boolean);

    if (words.length === 1) {
      return words[0].slice(0, 1).toUpperCase();
    }

    return `${words[0]?.[0] || ""}${words[1]?.[0] || ""}`.toUpperCase();
  }, [user.fullName]);

  const handleManualRefresh = () => {
    const controller = new AbortController();
    fetchDashboard(controller.signal, true);
  };

  return (
    <>
      <Navbar />

      <section className="dashboard-section">
        <div className="dashboard-container">
          <div className="dashboard-topbar">
            <div className="dashboard-topbar-left">
              <span className="dashboard-badge">User Dashboard</span>

              <p className="dashboard-mini-text">
                Overview of your domains, orders, account activity, payments,
                hosting, and quick tools.
              </p>
            </div>

            <div className="dashboard-profile-chip">
              <div className="dashboard-profile-avatar">{userInitials}</div>

              <div className="dashboard-profile-text">
                <strong>{user.fullName}</strong>
                <span>{hostingService?.planName || user.plan}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-realtime-row">
            <div className="dashboard-realtime-chip">
              <span
                className={
                  realtimeStatus === "connected"
                    ? "dashboard-realtime-dot online"
                    : "dashboard-realtime-dot offline"
                }
              />

              <span>{getRealtimeLabel(realtimeStatus)}</span>
            </div>

            <button
              type="button"
              className="dashboard-refresh-btn"
              onClick={handleManualRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {error ? (
            <div className="dashboard-error-box" role="alert">
              <p>{error}</p>
            </div>
          ) : null}

          <div className="dashboard-hero">
            <div className="dashboard-hero-left">
              <h1>
                {greeting}, <span>{user.fullName || "User"}</span>
              </h1>

              <p>
                Manage your domains, hosting, payments, orders, favorites, wallet
                balance, and account activity from one clean dashboard.
              </p>

              <div className="dashboard-user-meta">
                <div className="dashboard-meta-card">
                  <strong>Email</strong>
                  <span>{user.email || "Not available"}</span>
                </div>

                <div className="dashboard-meta-card">
                  <strong>Status</strong>
                  <span>{user.status || "Active User"}</span>
                </div>

                <div className="dashboard-meta-card">
                  <strong>Membership</strong>
                  <span>{user.joined || "Member since 2026"}</span>
                </div>
              </div>
            </div>

            <div className="dashboard-hero-right">
              <div className="dashboard-search-card">
                <span className="dashboard-card-tag">Smart Search</span>

                <h3>Find your next domain</h3>

                <p>
                  Search premium, brandable, affordable, and business-ready
                  domain names.
                </p>

                <div className="dashboard-search-actions">
                  <Link to="/domains" className="dashboard-primary-btn">
                    Search Domains
                  </Link>

                  <Link to="/payment-history" className="dashboard-secondary-btn">
                    View Payments
                  </Link>
                </div>

                <div className="dashboard-search-suggestions">
                  <span>.com</span>
                  <span>.in</span>
                  <span>.org</span>
                  <span>.net</span>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-stats-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, index) => (
                  <div
                    className="dashboard-stat-card dashboard-skeleton-card"
                    key={`dashboard-skeleton-${index}`}
                  >
                    <div className="dashboard-skeleton-line dashboard-skeleton-sm" />
                    <div className="dashboard-skeleton-line dashboard-skeleton-lg" />
                    <div className="dashboard-skeleton-line dashboard-skeleton-md" />
                  </div>
                ))
              : stats.map((item) => (
                  <Link
                    to={item.link}
                    className="dashboard-stat-card"
                    key={item.id || item.title}
                  >
                    <div className="dashboard-stat-top">
                      <span className="dashboard-stat-icon">{item.icon}</span>
                      <span className="dashboard-stat-title">{item.title}</span>
                    </div>

                    <h2>{item.value}</h2>

                    <p>{item.desc}</p>

                    <span className="dashboard-stat-trend">{item.trend}</span>
                  </Link>
                ))}
          </div>

          {/* ================= HOSTING SERVICE CARD ================= */}
          <div className="dashboard-service-card">
            <div className="dashboard-service-head">
              <div>
                <span className="dashboard-card-tag">My Hosting Service</span>

                <h3>
                  {hostingService
                    ? hostingService.planName
                    : "No active hosting plan"}
                </h3>

                <p>
                  {hostingService
                    ? hostingService.message
                    : "Choose a hosting plan to activate website hosting services."}
                </p>
              </div>

              <span
                className={`dashboard-service-status ${
                  hostingService
                    ? getSetupClass(hostingService.setupStatus)
                    : "pending"
                }`}
              >
                {hostingService
                  ? hostingService.setupStatusLabel
                  : "Not Active"}
              </span>
            </div>

            {hostingService ? (
              <>
                <div className="dashboard-service-grid">
                  <div>
                    <strong>Websites</strong>
                    <span>{hostingService.websiteLimit || 0}</span>
                  </div>

                  <div>
                    <strong>Storage</strong>
                    <span>{hostingService.storageLimit || "Included"}</span>
                  </div>

                  <div>
                    <strong>Mailboxes</strong>
                    <span>{hostingService.mailboxLimit || 0}</span>
                  </div>

                  <div>
                    <strong>SSL</strong>
                    <span>
                      {hostingService.sslEnabled ? "Included" : "Not included"}
                    </span>
                  </div>

                  <div>
                    <strong>Billing</strong>
                    <span>{hostingService.billingCycle}</span>
                  </div>

                  <div>
                    <strong>Expires</strong>
                    <span>{hostingService.expiresAt || "Not available"}</span>
                  </div>
                </div>

                <div className="dashboard-service-access">
                  {hostingService.cpanelUrl ? (
                    <a
                      href={hostingService.cpanelUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="dashboard-primary-btn"
                    >
                      Open cPanel
                    </a>
                  ) : (
                    <Link to="/hosting/setup" className="dashboard-primary-btn">
                      Complete Setup
                    </Link>
                  )}

                  <Link to="/dns-manage" className="dashboard-secondary-btn">
                    Manage DNS
                  </Link>

                  <Link to="/email" className="dashboard-secondary-btn">
                    Setup Email
                  </Link>
                </div>

                {!hostingService.cpanelUrl ? (
                  <div className="dashboard-service-note">
                    Your payment is successful and your hosting plan is active.
                    Complete the setup form to submit your domain and website
                    details. cPanel/server access will appear here after setup
                    completion.
                  </div>
                ) : null}
              </>
            ) : (
              <div className="dashboard-service-empty">
                <Link to="/hosting" className="dashboard-primary-btn">
                  Choose Hosting Plan
                </Link>
              </div>
            )}
          </div>

          <div className="dashboard-highlight-grid">
            <div className="dashboard-highlight-card">
              <div className="dashboard-highlight-icon">🚀</div>

              <div>
                <h4>
                  {hostingService ? "Hosting Active" : "Recommended Upgrade"}
                </h4>

                <p>
                  {hostingService
                    ? `${hostingService.planName} is active with ${hostingService.setupStatusLabel} status.`
                    : "Unlock premium hosting benefits for your account."}
                </p>
              </div>

              <Link
                to={hostingService?.cpanelUrl ? "/hosting" : "/hosting/setup"}
                className="dashboard-inline-link"
              >
                {hostingService?.cpanelUrl ? "Manage Hosting" : "Complete Setup"}
              </Link>
            </div>

            <div className="dashboard-highlight-card">
              <div className="dashboard-highlight-icon">💳</div>

              <div>
                <h4>Payments & Invoices</h4>

                <p>
                  Total successful payments:{" "}
                  {summary.totalPaymentAmountFormatted || "₹0"}
                </p>
              </div>

              <Link to="/payment-history" className="dashboard-inline-link">
                Open Payments
              </Link>
            </div>
          </div>

          <div className="dashboard-bottom-grid">
            <div className="dashboard-panel dashboard-activity-panel">
              <div className="dashboard-panel-head">
                <h3>Recent Activity</h3>

                <Link to="/activity" className="dashboard-panel-link">
                  View All
                </Link>
              </div>

              <div className="dashboard-activity-list">
                {!loading && recentActivities.length > 0 ? (
                  recentActivities.map((activity) => (
                    <div className="dashboard-activity-item" key={activity.id}>
                      <div className="dashboard-activity-icon">
                        {getActivityIcon(activity.type)}
                      </div>

                      <div className="dashboard-activity-content">
                        <h4>{activity.title}</h4>
                        <p>{activity.desc}</p>
                      </div>

                      <span className="dashboard-activity-time">
                        {activity.time}
                      </span>
                    </div>
                  ))
                ) : !loading ? (
                  <div className="dashboard-empty-state">
                    <p>No recent activity found yet.</p>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="dashboard-panel dashboard-actions-panel">
              <div className="dashboard-panel-head">
                <h3>Quick Actions</h3>
              </div>

              <div className="dashboard-actions-grid">
                {quickActions.map((action) => (
                  <Link
                    to={action.link}
                    className="dashboard-action-card"
                    key={action.title}
                  >
                    <div className="dashboard-action-top">
                      <h4>{action.title}</h4>

                      <span className="dashboard-action-badge">
                        {action.badge}
                      </span>
                    </div>

                    <p>{action.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {(recentPayments.length > 0 || recentDomains.length > 0) && (
            <div className="dashboard-bottom-grid dashboard-extra-grid">
              <div className="dashboard-panel">
                <div className="dashboard-panel-head">
                  <h3>Recent Payments</h3>
                  <Link to="/payment-history" className="dashboard-panel-link">
                    View All
                  </Link>
                </div>

                <div className="dashboard-mini-list">
                  {recentPayments.slice(0, 4).map((payment) => (
                    <div className="dashboard-mini-item" key={payment.id}>
                      <strong>{payment.planName}</strong>
                      <span>{payment.amount}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="dashboard-panel">
                <div className="dashboard-panel-head">
                  <h3>Recent Domains</h3>
                  <Link to="/domains" className="dashboard-panel-link">
                    View All
                  </Link>
                </div>

                <div className="dashboard-mini-list">
                  {recentDomains.slice(0, 4).map((domain) => (
                    <div className="dashboard-mini-item" key={domain.id}>
                      <strong>{domain.name}</strong>
                      <span>{domain.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default UserDashboard;